import {useApiQuery, LogUtils, ConfigObjectType, ArrayUtils} from '@wallaby-ui/core';
import {useEffect, useMemo} from 'react';
import {constants} from 'src/js/common';
import {WallabyCoreSchema} from 'src/js/common/types';
import {useAppDispatch, useAppSelector} from 'src/js/store_hooks';

import {MEDICATIONS_REDUCER_STATE_ID} from '../medications_constants';
import {medicationsSlice} from '../medications_slice';

const logger = LogUtils.getLogger('useLoadMedications', constants.LIBRARY_NAME);

interface UseLoadMedicationAtcsReturnValue {
  isLoading: boolean;
  error: unknown;
}

export function useLoadMedicationAtcs(apiUrl: string): UseLoadMedicationAtcsReturnValue {
  const isInitialized = useAppSelector((state) => state[MEDICATIONS_REDUCER_STATE_ID].isInitialized);

  const {data, isLoading, error} = useApiQuery<WallabyCoreSchema.components['schemas']['AtcHierarchyNodeWithParentDto'][]>([apiUrl], null, {
    axiosConfig: {url: apiUrl},
    reactQueryOptions: {
      enabled: !isInitialized,
    },
  });

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (data && !isInitialized) {
      const mappedData = ArrayUtils.computeHierarchyMetadata(data, (v) => ({id: v.id, parentId: v.parentId}));
      dispatch(medicationsSlice.actions.addFilterData({filterData: mappedData}));
    }
  }, [data, dispatch, isInitialized]);

  useEffect(() => {
    if (error) {
      logger.warn(`Error when loading medications!\n ${error}`);
    }
  }, [error]);

  return {isLoading, error};
}

// NOTE: we can't export these types for some reason
type MedicationAtc = WallabyCoreSchema.components['schemas']['AtcHierarchyNodeWithParentDto']; // eslint-disable-line
export function useMedicationAtcs<T>(config: ConfigObjectType) {
  const {isLoading, error} = useLoadMedicationAtcs(config.service.url.medicationsFilterData);
  const data = useAppSelector((state) => state[MEDICATIONS_REDUCER_STATE_ID].filterData) as T[];
  return {data, isLoading, error};
}
export function useMedicationAtcsByMedicationId<T>(config: ConfigObjectType, medicationId: number | string | undefined | null) {
  const medicationsAtcsByMedicationIdUrl = config.service.url.medicationAtcGroupsByMedicationId.replace('{id}', medicationId);
  const medicationAtcsByMedicationIdParams = {
    removeTransitiveParents: 'true',
  };
  const {data: rawData, isLoading} = useApiQuery<T[]>([medicationsAtcsByMedicationIdUrl], null, {
    axiosConfig: {
      url: medicationsAtcsByMedicationIdUrl,
      params: new URLSearchParams(medicationAtcsByMedicationIdParams),
    },
    reactQueryOptions: {
      enabled: medicationId != null,
    },
  });
  const mappedData = useMemo(() => {
    return ArrayUtils.computeHierarchyMetadata((rawData ?? []) as MedicationAtc[], (v: MedicationAtc) => ({
      id: v.id,
      parentId: v.parentId,
    }));
  }, [rawData]);
  return {data: mappedData, isLoading};
}

type MedicationsTypeParam = 'MEDICINAL_PRODUCT_GROUP' | 'MEDICINAL_PRODUCT' | 'ACTIVE_INGREDIENT';
type MedicationType = WallabyCoreSchema.components['schemas']['MedicationExportDto'];
export function useMedications(config: ConfigObjectType, type: MedicationsTypeParam) {
  const medicationsUrl = config.service.url.medications;
  const medicationsParams = {type};
  const {data, isLoading} = useApiQuery<MedicationType[]>([medicationsUrl, type], null, {
    axiosConfig: {
      url: medicationsUrl,
      params: new URLSearchParams(medicationsParams),
    },
  });
  return {data: data ?? [], medicationsLoading: isLoading};
}
